import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, Code, Save, X } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

import { supabase } from "@/integrations/supabase/client";
import { notify } from "@/utils/notification";
import { createSafeHTML } from "@/utils/sanitization";

interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  template_type: string;
  content_html: string;
  content_text: string;
  is_active?: boolean;
}

interface EmailTemplateFormProps {
  template?: EmailTemplate;
  onSave: (template: EmailTemplate) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const EmailTemplateForm: React.FC<EmailTemplateFormProps> = ({
  template,
  onSave,
  onCancel,
  isLoading = false
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<EmailTemplate>({
    name: '',
    subject: '',
    template_type: 'custom',
    content_html: '',
    content_text: '',
    is_active: true
  });

  const [activeTab, setActiveTab] = useState('visual');

  useEffect(() => {
    if (template) {
      setFormData(template);
    }
  }, [template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleHtmlChange = (html: string) => {
    setFormData(prev => ({ ...prev, content_html: html }));
  };

  const predefinedTemplates = [
    {
      name: 'Welcome Email',
      subject: 'Welcome to Our Platform!',
      content_html: `
        <h1>Welcome {{first_name}}!</h1>
        <p>Thank you for joining our platform. We're excited to have you on board.</p>
        <p>Here's what you can do next:</p>
        <ul>
          <li>Complete your profile</li>
          <li>Browse our services</li>
          <li>Book your first session</li>
        </ul>
        <p>If you have any questions, feel free to reach out to our support team.</p>
        <p>Best regards,<br>The Team</p>
        <p><a href="{{unsubscribe_link}}">Unsubscribe</a></p>
      `,
      content_text: `Welcome {{first_name}}!\n\nThank you for joining our platform. We're excited to have you on board.\n\nHere's what you can do next:\n- Complete your profile\n- Browse our services\n- Book your first session\n\nIf you have any questions, feel free to reach out to our support team.\n\nBest regards,\nThe Team\n\nUnsubscribe: {{unsubscribe_link}}`
    },
    {
      name: 'Booking Confirmation',
      subject: 'Your Booking is Confirmed - {{date}}',
      content_html: `
        <h1>Booking Confirmed!</h1>
        <p>Hi {{first_name}},</p>
        <p>Your booking has been confirmed for <strong>{{date}}</strong>.</p>
        <div style="border: 1px solid #ddd; padding: 20px; margin: 20px 0; background: #f9f9f9;">
          <h3>Booking Details:</h3>
          <p><strong>Service:</strong> {{service_name}}</p>
          <p><strong>Date & Time:</strong> {{date}}</p>
          <p><strong>Practitioner:</strong> {{practitioner_name}}</p>
          <p><strong>Format:</strong> {{format}}</p>
        </div>
        <p>We look forward to seeing you!</p>
        <p>Best regards,<br>The Team</p>
        <p><a href="{{unsubscribe_link}}">Unsubscribe</a></p>
      `,
      content_text: `Booking Confirmed!\n\nHi {{first_name}},\n\nYour booking has been confirmed for {{date}}.\n\nBooking Details:\nService: {{service_name}}\nDate & Time: {{date}}\nPractitioner: {{practitioner_name}}\nFormat: {{format}}\n\nWe look forward to seeing you!\n\nBest regards,\nThe Team\n\nUnsubscribe: {{unsubscribe_link}}`
    },
    {
      name: 'Newsletter',
      subject: 'Our Monthly Newsletter - {{date}}',
      content_html: `
        <h1>Monthly Newsletter</h1>
        <p>Hi {{first_name}},</p>
        <p>Here's what's new this month:</p>
        
        <h2>Featured Articles</h2>
        <ul>
          <li><a href="#">How to Improve Your Mental Health</a></li>
          <li><a href="#">Benefits of Regular Therapy Sessions</a></li>
          <li><a href="#">Tips for Better Sleep</a></li>
        </ul>
        
        <h2>New Practitioners</h2>
        <p>We're excited to welcome new practitioners to our platform...</p>
        
        <h2>Upcoming Events</h2>
        <p>Don't miss our upcoming webinar on wellness...</p>
        
        <p>Thank you for being part of our community!</p>
        <p>Best regards,<br>The Team</p>
        <p><a href="{{unsubscribe_link}}">Unsubscribe</a></p>
      `,
      content_text: `Monthly Newsletter\n\nHi {{first_name}},\n\nHere's what's new this month:\n\nFeatured Articles:\n- How to Improve Your Mental Health\n- Benefits of Regular Therapy Sessions\n- Tips for Better Sleep\n\nNew Practitioners:\nWe're excited to welcome new practitioners to our platform...\n\nUpcoming Events:\nDon't miss our upcoming webinar on wellness...\n\nThank you for being part of our community!\n\nBest regards,\nThe Team\n\nUnsubscribe: {{unsubscribe_link}}`
    }
  ];

  const loadPredefinedTemplate = (templateData: any) => {
    setFormData(prev => ({
      ...prev,
      name: templateData.name,
      subject: templateData.subject,
      content_html: templateData.content_html,
      content_text: templateData.content_text
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Start Templates</CardTitle>
          <CardDescription>Choose from our pre-built templates or start from scratch</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {predefinedTemplates.map((template, index) => (
              <div
                key={index}
                className="border rounded-lg p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => loadPredefinedTemplate(template)}
              >
                <h4 className="font-medium">{template.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{template.subject}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Template Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter template name"
                required
              />
            </div>
            <div>
              <Label htmlFor="type">Template Type</Label>
              <Select 
                value={formData.template_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter email subject"
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Content Editor */}
      <Card>
        <CardHeader>
          <CardTitle>Email Content</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="visual">{t('dashboard:reportsManager.visualEditor')}</TabsTrigger>
              <TabsTrigger value="html">{t('dashboard:reportsManager.htmlCode')}</TabsTrigger>
              <TabsTrigger value="text">{t('dashboard:reportsManager.plainText')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="visual" className="mt-4">
              <RichTextEditor
                value={formData.content_html}
                onChange={handleHtmlChange}
                placeholder="Create your email content using the visual editor..."
              />
            </TabsContent>
            
            <TabsContent value="html" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="content_html">HTML Content</Label>
                <Textarea
                  id="content_html"
                  value={formData.content_html}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_html: e.target.value }))}
                  placeholder="Enter HTML content"
                  rows={15}
                  className="font-mono text-sm"
                />
              </div>
            </TabsContent>
            
            <TabsContent value="text" className="mt-4">
              <div className="space-y-2">
                <Label htmlFor="content_text">Plain Text Content</Label>
                <Textarea
                  id="content_text"
                  value={formData.content_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_text: e.target.value }))}
                  placeholder="Enter plain text content (optional - will be auto-generated if empty)"
                  rows={10}
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg p-4 bg-white">
            <div className="border-b pb-2 mb-4">
              <div className="text-sm text-gray-600">Subject: {formData.subject}</div>
            </div>
            <div 
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={createSafeHTML(formData.content_html)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          <Save className="h-4 w-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Template'}
        </Button>
      </div>
    </form>
  );
};

export default EmailTemplateForm;